import { RestartAlt } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  SxProps,
  Typography,
} from '@mui/material';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { useSxStyles } from '~/features/ScheduledEmploymentChanges/Common/ScheduledChangesDialogWithTitleAndActions/useStyles';
import { ScheduleManualRevalidate } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import { mergeSxProps } from '~/utils/helpers/sxProps';

type PropsType = {
  open: boolean;
  onClose: () => void;
  children?: ReactNode;
  sx?: SxProps;
  form: {
    hasFormErrors: boolean;
    submit: {
      isSubmitting: boolean;
      onSubmit: (e: React.MouseEvent<HTMLButtonElement>) => void;
      disabled?: boolean;
    };
  };
  manualRevalidate?: ScheduleManualRevalidate;
};

export const ScheduledChangesDialogWithTitleAndActions = (props: PropsType) => {
  const {
    open,
    onClose,
    children,
    form: {
      hasFormErrors,
      submit: { isSubmitting: submitting, onSubmit, disabled },
    },
    manualRevalidate,
    sx,
  } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  return (
    <DraggableDialog
      open={open}
      onClose={onClose}
      maxWidth={'lg'}
      slotProps={{
        paper: {
          sx: mergeSxProps(sxStyles.dialogPaper, sx),
        },
      }}
      disableBackdropClick={true}
    >
      <SolaForceDialogTitle
        icon={<LegacyFAIcon FAIcon={LegacyFAIcons.CalendarPlus} />}
        onClose={onClose}
      >
        {t('laSchedule')}
      </SolaForceDialogTitle>
      <DialogContent dividers={true}>{children}</DialogContent>
      <DialogActions>
        {hasFormErrors && (
          <>
            <Typography color={'error'} sx={sxStyles.formErrorMessage}>
              {t('formValidationFailed')}
            </Typography>
            {!!manualRevalidate && manualRevalidate?.available && (
              <>
                <Button
                  onClick={manualRevalidate.onClearFormErrors}
                  data-testid="clear-errors-schedule-changes-button"
                  color={'error'}
                  startIcon={<RestartAlt />}
                >
                  {t('clearErrors')}
                </Button>
                <Button
                  onClick={manualRevalidate.onRevalidateForm}
                  data-testid="revalidate-schedule-changes-button"
                  color={'error'}
                  startIcon={<RestartAlt />}
                >
                  {t('revalidate')}
                </Button>
              </>
            )}
          </>
        )}

        <Button
          type={'submit'}
          onClick={onSubmit}
          data-testid="save-schedule-changes-button"
          disabled={submitting || hasFormErrors || disabled}
          startIcon={
            submitting ? (
              <CircularProgress size={20} />
            ) : (
              <LegacyFAIcon FAIcon={LegacyFAIcons.Save} />
            )
          }
        >
          {t('save')}
        </Button>
        <Button
          onClick={onClose}
          startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Close} />}
          data-testid="cancel-schedule-changes-button"
        >
          {t('cancel')}
        </Button>
      </DialogActions>
    </DraggableDialog>
  );
};
